<script setup>

import {onMounted, onUnmounted, ref} from "vue";

const EMOJIS = ['❄️','❅', '❄︎', '❄️']
const SANTA_LADA = '/santa-lada.png'
const SANTA_LADA_RATIO = 0.005
const MAX_FPS = 60

const canvasRef = ref(null)
// context, window width, window height, device pixel ratio
let ctx, w, h, dpr
let rafId = 0
let sprites = []
let particles = []
let lastFrame = 0
let reducedMotion = false
let pageHidden = false

/**
 * Picks a random element from an array.
 * @param arr
 * @returns {*}
 */
function pick(arr) {
  return arr[(Math.random() * arr.length) | 0]
}

/**
 * Adjusts the canvas element's size and resolution according to the current window dimensions and device pixel ratio.
 * Scales the canvas width and height proportionally to provide better visual quality.
 *
 * @return {void} Does not return any value.
 */
function resize() {
  const c = canvasRef.value
  if (!c) return
  dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1))
  w = Math.floor(window.innerWidth)
  h = Math.floor(window.innerHeight)
  c.width = Math.floor(w * dpr)
  c.height = Math.floor(h * dpr)
  c.style.width = w + 'px'
  c.style.height = h + 'px'
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
}

/**
 * Creates an emoji sprite using a canvas element with the specified emoji and pixel size.
 *
 * @param {string} emoji - The emoji character to render onto the canvas.
 * @param {number} px - The font size in pixels for rendering the emoji.
 * @return {Object} An object representing the emoji sprite, containing the canvas element, its width, height, and type.
 */
function makeEmojiSprite(emoji, px) {
  const off = document.createElement('canvas')
  const octx = off.getContext('2d')
  const size = Math.ceil(px * 1.5)
  off.width = size
  off.height = size
  octx.font = `${px}px system-ui, Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif`
  octx.fillStyle = 'gray'
  octx.textBaseline = 'top'
  octx.fillText(emoji, Math.floor(px * 0.1), Math.floor(px * 0.1))
  return { type: 'emoji', canvas: off, w: off.width, h: off.height }
}

/**
 * Loads an image from a specified URL, scales it to fit within the specified pixel size,
 * and draws it to an offscreen canvas.
 *
 * @param {string} url - The URL of the image to load.
 * @param {number} px - The maximum pixel size for the scaled image.
 * @return {Promise<Object|null>} A promise that resolves to an object containing the type,
 * the canvas with the scaled image, and its width and height, or resolves to null on error.
 */
function loadImageSprite(url, px) {
  return new Promise(resolve => {
    const img = new Image()
    img.decoding = 'async'
    img.loading = 'eager'
    img.src = url
    img.onload = () => {
      const scale = px / Math.max(img.width, img.height)
      const w = Math.max(8, Math.floor(img.width * scale))
      const h = Math.max(8, Math.floor(img.height * scale))
      const off = document.createElement('canvas')
      off.width = w
      off.height = h
      const octx = off.getContext('2d', { alpha: true })
      octx.drawImage(img, 0, 0, w, h)
      resolve({ type: 'image', canvas: off, w, h })
    }
    img.onerror = () => resolve(null)
  })
}

/**
 * Constructs and populates an array of sprites using predefined sizes and a set of emojis.
 * This method generates emoji-based sprites at various sizes and stores them in the `sprites` array.
 *
 * @return {void} Does not return a value.
 */
function buildSprites() {
  sprites = []
  // Prepare a few common sizes to reuse across particles
  const sizes = [18, 22, 26, 30]
  for (const s of sizes) {
    for (const e of EMOJIS) {
      sprites.push(makeEmojiSprite(e, s))
    }
  }
}

/**
 * Calculates the number of particles to be rendered based on the window's width.
 * The number scales with the width of the window, while being clamped to a
 * range to ensure optimal performance and avoid overloading the CPU.
 *
 * @return {number} The calculated particle count, clamped between 20 and 70.
 */
function particleCount() {
  // Scale with width, clamp to keep CPU low
  const base = Math.floor(window.innerWidth / 40)
  return Math.max(10, Math.min(35, base))
}

/**
 * Initializes particles with specific properties such as position, velocity, and sprite type.
 *
 * @param {Object} imageSprite - The image sprite to be optionally used for particles. If provided, some particles will use this sprite based on a predefined ratio.
 * @return {void} No return value. The function modifies the `particles` array with initialized particle objects.
 */
function initParticles(imageSprite) {
  const N = particleCount()
  particles = Array.from({ length: N }, () => {
    const isImage = imageSprite && Math.random() < SANTA_LADA_RATIO
    const sprite = isImage ? imageSprite : pick(sprites)
    const speed = 0.4 + Math.random() * 0.8
    const swayAmp = 8 + Math.random() * 20
    const swayFreq = 0.6 + Math.random() * 1.2

    return {
      sprite,
      x: Math.random() * w,
      y: Math.random() * h,
      vy: speed,
      ax: (Math.random() < 0.5 ? -1 : 1) * swayAmp,
      t: Math.random() * 100,
      f: swayFreq,
    }
  })
}

/**
 * Updates the animation state and renders the particles for each frame.
 * Handles the frame rate capping, checks for environmental conditions
 * like reduced motion or hidden page, and animates particles accordingly.
 *
 * @param {number} ts - The timestamp of the current frame provided by the
 * requestAnimationFrame callback.
 * @return {void} Does not return a value.
 */
function tick(ts) {
  if (reducedMotion || pageHidden) return // paused
  rafId = requestAnimationFrame(tick)
  if (!ctx) return

  // FPS cap
  const minDelta = 1000 / MAX_FPS
  if (ts - lastFrame < minDelta) return
  lastFrame = ts

  ctx.clearRect(0, 0, w, h)
  ctx.globalAlpha = 1

  for (const p of particles) {
    p.t += 0.016
    p.y += p.vy
    p.x += Math.sin(p.t * p.f) * 0.3 + (p.ax * 0.001)

    // recycle
    if (p.y > h + 12) { p.y = -12; p.x = Math.random() * w }

    ctx.drawImage(p.sprite.canvas, p.x, p.y)
  }
}

/**
 * Initializes the canvas context, sets up sprites, loads an image sprite,
 * initializes particle effects, and starts the animation loop if reduced motion
 * is not enabled.
 *
 * @return {Promise<void>} A promise that resolves when the method execution is complete.
 */
async function start() {
  if (!canvasRef.value) return
  ctx = canvasRef.value.getContext('2d', { alpha: true, desynchronized: true })
  resize()
  buildSprites()
  const imageSprite = await loadImageSprite(SANTA_LADA, 40)
  initParticles(imageSprite || null)
  if (!reducedMotion) rafId = requestAnimationFrame(tick)
}

function stop() {
  cancelAnimationFrame(rafId)
  rafId = 0
}

function onVisibility() {
  pageHidden = document.visibilityState !== 'visible'
  if (pageHidden) stop()
  else if (!rafId && !reducedMotion) rafId = requestAnimationFrame(tick)
}

onMounted(() => {
  reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  window.addEventListener('resize', resize)
  document.addEventListener('visibilitychange', onVisibility)
  start()
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibility)
  window.removeEventListener('resize', resize)
  stop()
})

</script>

<template>
  <canvas ref="canvasRef" class="christmasFx" aria-hidden="true" />
</template>

<style scoped>
.christmasFx{
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.5;
}

@media (prefers-reduced-motion: reduce) {
  .christmasFx { display: none; }
}

</style>